import { UIEvent, useState } from "react";
import { BoxProps, Box, Divider, Flex } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { IKeyFiguresData } from "src/api/server/extended-search";
import { ArrowLink } from "src/components/base/arrow-link";
import { Button } from "src/components/base/button";
import { ISearch } from "src/components/pages/search/search.types";
import { handleResetKeyFigures } from "src/components/pages/search/store/actions";
import { KeyFiguresGroup } from "./components/key-figures-group";

interface IKeyFiguresFilterBodyContentProps extends ISearch, BoxProps {
    keyFiguresData: IKeyFiguresData;
}

export const KeyFiguresFilterBodyContent = ({ keyFiguresData, onSearch }: IKeyFiguresFilterBodyContentProps) => {
    const [showBlur, setShowBlur] = useState<boolean>(true);
    const { t } = useTranslation("extended-search");
    const { faqLink, keyFiguresGroups } = keyFiguresData;

    const handleReset = () => {
        handleResetKeyFigures(onSearch);
    };

    const handleBlurWhenOnScroll = (event: UIEvent) => {
        const element = event.target as Element;
        const THRESHOLD = 30;
        const startedScrolling = element.scrollTop > THRESHOLD;

        setShowBlur(!startedScrolling);
    };

    return (
        <>
            <Box
                height="480px"
                overflow="auto"
                p={3}
                sx={{
                    scrollbarWidth: "thin",
                    WebkitPaddingEnd: "8px",
                    "&::-webkit-scrollbar": {
                        width: "2",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "white",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        border: "4px solid white",
                        borderRadius: "8px",
                        backgroundColor: "gray.200",
                    },
                }}
                onScroll={handleBlurWhenOnScroll}
            >
                {keyFiguresGroups.map((keyFiguresGroup, index) => (
                    <KeyFiguresGroup
                        key={keyFiguresGroup.headline}
                        keyFiguresGroup={keyFiguresGroup}
                        onSearch={onSearch}
                        pb={index !== keyFiguresGroups.length - 1 ? 2 : 0}
                    />
                ))}
            </Box>

            {showBlur && (
                <Box
                    position="absolute"
                    top="448px"
                    height={4}
                    width="calc(100% - 16px)"
                    background="linear-gradient(180deg, white -4.68%, rgba(255, 255, 255, 0.6) 33.79%, white 79.69%)"
                ></Box>
            )}

            <Box p={3} pt={0}>
                <Divider borderColor="gray.100" mb={2} />
                <Flex justifyContent="space-between">
                    <Button variant="ghost-underline" size="sm" ml={-2} onClick={handleReset}>
                        {t("buttons.reset-key-figures")}
                    </Button>
                    {faqLink && (
                        <ArrowLink display="inline-flex" alignItems="center" href={faqLink.url} target={faqLink.target}>
                            {faqLink.name}
                        </ArrowLink>
                    )}
                </Flex>
            </Box>
        </>
    );
};
